import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-base focus-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        brand:
          "btn-glow bg-brand-primary text-white shadow-glow-primary hover:bg-brand-primary-dark hover:shadow-glow-primary hover:scale-[1.02]",
        accent:
          "bg-brand-accent text-white shadow-glow-accent hover:bg-brand-accent-dark hover:shadow-glow-accent hover:scale-[1.02]",
        destructive:
          "bg-brand-danger text-white shadow-sm hover:bg-brand-danger-dark hover:shadow-glow-danger",
        success:
          "bg-brand-success text-white shadow-sm hover:bg-brand-success-dark hover:shadow-glow-success",
        outline:
          "border-2 border-border bg-transparent hover:bg-background-hover hover:border-border-hover",
        "outline-brand":
          "border-2 border-brand-primary bg-transparent text-brand-primary hover:bg-brand-primary/10 hover:border-brand-primary-light",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-background-hover",
        link: "text-brand-primary underline-offset-4 hover:underline hover:text-brand-primary-light",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-11 rounded-lg px-8 text-base",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
